/**
 * Country-specific budget ranges based on local purchasing power
 * These values reflect what's considered a reasonable budget for an event in each country,
 * accounting for local cost of living and purchasing power, not just currency conversion.
 */

// Budget tiers: Budget, Moderate, Comfortable, Premium, Luxury
// These are approximate ranges that reflect local purchasing power

const BUDGET_RANGES = {
  // North America - High cost of living
  'US': {
    quickSelect: [250, 500, 750, 1000, 1500, 2000, 3000, 5000],
    min: 100,
    max: 10000,
    step: 50,
    tiers: {
      budget: { min: 100, max: 500, label: 'Budget-friendly' },
      moderate: { min: 500, max: 1500, label: 'Moderate' },
      comfortable: { min: 1500, max: 3000, label: 'Comfortable' },
      premium: { min: 3000, max: 5000, label: 'Premium' },
      luxury: { min: 5000, max: 10000, label: 'Luxury' }
    }
  },
  'CA': {
    quickSelect: [300, 600, 900, 1200, 1800, 2400, 3600, 6000],
    min: 120,
    max: 12000,
    step: 50,
    tiers: {
      budget: { min: 120, max: 600, label: 'Budget-friendly' },
      moderate: { min: 600, max: 1800, label: 'Moderate' },
      comfortable: { min: 1800, max: 3600, label: 'Comfortable' },
      premium: { min: 3600, max: 6000, label: 'Premium' },
      luxury: { min: 6000, max: 12000, label: 'Luxury' }
    }
  },
  'MX': {
    quickSelect: [2000, 4000, 6000, 8000, 12000, 16000, 24000, 40000],
    min: 800,
    max: 80000,
    step: 200,
    tiers: {
      budget: { min: 800, max: 4000, label: 'Budget-friendly' },
      moderate: { min: 4000, max: 12000, label: 'Moderate' },
      comfortable: { min: 12000, max: 24000, label: 'Comfortable' },
      premium: { min: 24000, max: 40000, label: 'Premium' },
      luxury: { min: 40000, max: 80000, label: 'Luxury' }
    }
  },
  
  // Europe - Varies by country
  'GB': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  // Eurozone countries - similar purchasing power
  'EU': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'DE': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'FR': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'IT': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'ES': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'NL': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'BE': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'AT': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'FI': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'IE': {
    quickSelect: [200, 400, 600, 800, 1200, 1600, 2400, 4000],
    min: 80,
    max: 8000,
    step: 40,
    tiers: {
      budget: { min: 80, max: 400, label: 'Budget-friendly' },
      moderate: { min: 400, max: 1200, label: 'Moderate' },
      comfortable: { min: 1200, max: 2400, label: 'Comfortable' },
      premium: { min: 2400, max: 4000, label: 'Premium' },
      luxury: { min: 4000, max: 8000, label: 'Luxury' }
    }
  },
  'PT': {
    quickSelect: [150, 300, 450, 600, 900, 1200, 1800, 3000],
    min: 60,
    max: 6000,
    step: 30,
    tiers: {
      budget: { min: 60, max: 300, label: 'Budget-friendly' },
      moderate: { min: 300, max: 900, label: 'Moderate' },
      comfortable: { min: 900, max: 1800, label: 'Comfortable' },
      premium: { min: 1800, max: 3000, label: 'Premium' },
      luxury: { min: 3000, max: 6000, label: 'Luxury' }
    }
  },
  'GR': {
    quickSelect: [150, 300, 450, 600, 900, 1200, 1800, 3000],
    min: 60,
    max: 6000,
    step: 30,
    tiers: {
      budget: { min: 60, max: 300, label: 'Budget-friendly' },
      moderate: { min: 300, max: 900, label: 'Moderate' },
      comfortable: { min: 900, max: 1800, label: 'Comfortable' },
      premium: { min: 1800, max: 3000, label: 'Premium' },
      luxury: { min: 3000, max: 6000, label: 'Luxury' }
    }
  },
  'CH': {
    quickSelect: [300, 600, 900, 1200, 1800, 2400, 3600, 6000],
    min: 120,
    max: 12000,
    step: 50,
    tiers: {
      budget: { min: 120, max: 600, label: 'Budget-friendly' },
      moderate: { min: 600, max: 1800, label: 'Moderate' },
      comfortable: { min: 1800, max: 3600, label: 'Comfortable' },
      premium: { min: 3600, max: 6000, label: 'Premium' },
      luxury: { min: 6000, max: 12000, label: 'Luxury' }
    }
  },
  'SE': {
    quickSelect: [2000, 4000, 6000, 8000, 12000, 16000, 24000, 40000],
    min: 800,
    max: 80000,
    step: 200,
    tiers: {
      budget: { min: 800, max: 4000, label: 'Budget-friendly' },
      moderate: { min: 4000, max: 12000, label: 'Moderate' },
      comfortable: { min: 12000, max: 24000, label: 'Comfortable' },
      premium: { min: 24000, max: 40000, label: 'Premium' },
      luxury: { min: 40000, max: 80000, label: 'Luxury' }
    }
  },
  'NO': {
    quickSelect: [2000, 4000, 6000, 8000, 12000, 16000, 24000, 40000],
    min: 800,
    max: 80000,
    step: 200,
    tiers: {
      budget: { min: 800, max: 4000, label: 'Budget-friendly' },
      moderate: { min: 4000, max: 12000, label: 'Moderate' },
      comfortable: { min: 12000, max: 24000, label: 'Comfortable' },
      premium: { min: 24000, max: 40000, label: 'Premium' },
      luxury: { min: 40000, max: 80000, label: 'Luxury' }
    }
  },
  'DK': {
    quickSelect: [1500, 3000, 4500, 6000, 9000, 12000, 18000, 30000],
    min: 600,
    max: 60000,
    step: 150,
    tiers: {
      budget: { min: 600, max: 3000, label: 'Budget-friendly' },
      moderate: { min: 3000, max: 9000, label: 'Moderate' },
      comfortable: { min: 9000, max: 18000, label: 'Comfortable' },
      premium: { min: 18000, max: 30000, label: 'Premium' },
      luxury: { min: 30000, max: 60000, label: 'Luxury' }
    }
  },
  'PL': {
    quickSelect: [800, 1600, 2400, 3200, 4800, 6400, 9600, 16000],
    min: 320,
    max: 32000,
    step: 80,
    tiers: {
      budget: { min: 320, max: 1600, label: 'Budget-friendly' },
      moderate: { min: 1600, max: 4800, label: 'Moderate' },
      comfortable: { min: 4800, max: 9600, label: 'Comfortable' },
      premium: { min: 9600, max: 16000, label: 'Premium' },
      luxury: { min: 16000, max: 32000, label: 'Luxury' }
    }
  },
  
  // Asia Pacific - Varies significantly
  'AU': {
    quickSelect: [300, 600, 900, 1200, 1800, 2400, 3600, 6000],
    min: 120,
    max: 12000,
    step: 50,
    tiers: {
      budget: { min: 120, max: 600, label: 'Budget-friendly' },
      moderate: { min: 600, max: 1800, label: 'Moderate' },
      comfortable: { min: 1800, max: 3600, label: 'Comfortable' },
      premium: { min: 3600, max: 6000, label: 'Premium' },
      luxury: { min: 6000, max: 12000, label: 'Luxury' }
    }
  },
  'NZ': {
    quickSelect: [300, 600, 900, 1200, 1800, 2400, 3600, 6000],
    min: 120,
    max: 12000,
    step: 50,
    tiers: {
      budget: { min: 120, max: 600, label: 'Budget-friendly' },
      moderate: { min: 600, max: 1800, label: 'Moderate' },
      comfortable: { min: 1800, max: 3600, label: 'Comfortable' },
      premium: { min: 3600, max: 6000, label: 'Premium' },
      luxury: { min: 6000, max: 12000, label: 'Luxury' }
    }
  },
  'JP': {
    quickSelect: [30000, 60000, 90000, 120000, 180000, 240000, 360000, 600000],
    min: 12000,
    max: 1200000,
    step: 3000,
    tiers: {
      budget: { min: 12000, max: 60000, label: 'Budget-friendly' },
      moderate: { min: 60000, max: 180000, label: 'Moderate' },
      comfortable: { min: 180000, max: 360000, label: 'Comfortable' },
      premium: { min: 360000, max: 600000, label: 'Premium' },
      luxury: { min: 600000, max: 1200000, label: 'Luxury' }
    }
  },
  'CN': {
    quickSelect: [1500, 3000, 4500, 6000, 9000, 12000, 18000, 30000],
    min: 600,
    max: 60000,
    step: 150,
    tiers: {
      budget: { min: 600, max: 3000, label: 'Budget-friendly' },
      moderate: { min: 3000, max: 9000, label: 'Moderate' },
      comfortable: { min: 9000, max: 18000, label: 'Comfortable' },
      premium: { min: 18000, max: 30000, label: 'Premium' },
      luxury: { min: 30000, max: 60000, label: 'Luxury' }
    }
  },
  'IN': {
    quickSelect: [10000, 20000, 30000, 40000, 60000, 80000, 120000, 200000],
    min: 4000,
    max: 400000,
    step: 1000,
    tiers: {
      budget: { min: 4000, max: 20000, label: 'Budget-friendly' },
      moderate: { min: 20000, max: 60000, label: 'Moderate' },
      comfortable: { min: 60000, max: 120000, label: 'Comfortable' },
      premium: { min: 120000, max: 200000, label: 'Premium' },
      luxury: { min: 200000, max: 400000, label: 'Luxury' }
    }
  },
  'SG': {
    quickSelect: [400, 800, 1200, 1600, 2400, 3200, 4800, 8000],
    min: 160,
    max: 16000,
    step: 40,
    tiers: {
      budget: { min: 160, max: 800, label: 'Budget-friendly' },
      moderate: { min: 800, max: 2400, label: 'Moderate' },
      comfortable: { min: 2400, max: 4800, label: 'Comfortable' },
      premium: { min: 4800, max: 8000, label: 'Premium' },
      luxury: { min: 8000, max: 16000, label: 'Luxury' }
    }
  },
  'MY': {
    quickSelect: [500, 1000, 1500, 2000, 3000, 4000, 6000, 10000],
    min: 200,
    max: 20000,
    step: 50,
    tiers: {
      budget: { min: 200, max: 1000, label: 'Budget-friendly' },
      moderate: { min: 1000, max: 3000, label: 'Moderate' },
      comfortable: { min: 3000, max: 6000, label: 'Comfortable' },
      premium: { min: 6000, max: 10000, label: 'Premium' },
      luxury: { min: 10000, max: 20000, label: 'Luxury' }
    }
  },
  'TH': {
    quickSelect: [3000, 6000, 9000, 12000, 18000, 24000, 36000, 60000],
    min: 1200,
    max: 120000,
    step: 300,
    tiers: {
      budget: { min: 1200, max: 6000, label: 'Budget-friendly' },
      moderate: { min: 6000, max: 18000, label: 'Moderate' },
      comfortable: { min: 18000, max: 36000, label: 'Comfortable' },
      premium: { min: 36000, max: 60000, label: 'Premium' },
      luxury: { min: 60000, max: 120000, label: 'Luxury' }
    }
  },
  'PH': {
    quickSelect: [5000, 10000, 15000, 20000, 30000, 40000, 60000, 100000],
    min: 2000,
    max: 200000,
    step: 500,
    tiers: {
      budget: { min: 2000, max: 10000, label: 'Budget-friendly' },
      moderate: { min: 10000, max: 30000, label: 'Moderate' },
      comfortable: { min: 30000, max: 60000, label: 'Comfortable' },
      premium: { min: 60000, max: 100000, label: 'Premium' },
      luxury: { min: 100000, max: 200000, label: 'Luxury' }
    }
  },
  'ID': {
    quickSelect: [2000000, 4000000, 6000000, 8000000, 12000000, 16000000, 24000000, 40000000],
    min: 800000,
    max: 80000000,
    step: 200000,
    tiers: {
      budget: { min: 800000, max: 4000000, label: 'Budget-friendly' },
      moderate: { min: 4000000, max: 12000000, label: 'Moderate' },
      comfortable: { min: 12000000, max: 24000000, label: 'Comfortable' },
      premium: { min: 24000000, max: 40000000, label: 'Premium' },
      luxury: { min: 40000000, max: 80000000, label: 'Luxury' }
    }
  },
  'KR': {
    quickSelect: [300000, 600000, 900000, 1200000, 1800000, 2400000, 3600000, 6000000],
    min: 120000,
    max: 12000000,
    step: 30000,
    tiers: {
      budget: { min: 120000, max: 600000, label: 'Budget-friendly' },
      moderate: { min: 600000, max: 1800000, label: 'Moderate' },
      comfortable: { min: 1800000, max: 3600000, label: 'Comfortable' },
      premium: { min: 3600000, max: 6000000, label: 'Premium' },
      luxury: { min: 6000000, max: 12000000, label: 'Luxury' }
    }
  },
  'VN': {
    quickSelect: [2000000, 4000000, 6000000, 8000000, 12000000, 16000000, 24000000, 40000000],
    min: 800000,
    max: 80000000,
    step: 200000,
    tiers: {
      budget: { min: 800000, max: 4000000, label: 'Budget-friendly' },
      moderate: { min: 4000000, max: 12000000, label: 'Moderate' },
      comfortable: { min: 12000000, max: 24000000, label: 'Comfortable' },
      premium: { min: 24000000, max: 40000000, label: 'Premium' },
      luxury: { min: 40000000, max: 80000000, label: 'Luxury' }
    }
  },
  
  // Middle East
  'AE': {
    quickSelect: [800, 1600, 2400, 3200, 4800, 6400, 9600, 16000],
    min: 320,
    max: 32000,
    step: 80,
    tiers: {
      budget: { min: 320, max: 1600, label: 'Budget-friendly' },
      moderate: { min: 1600, max: 4800, label: 'Moderate' },
      comfortable: { min: 4800, max: 9600, label: 'Comfortable' },
      premium: { min: 9600, max: 16000, label: 'Premium' },
      luxury: { min: 16000, max: 32000, label: 'Luxury' }
    }
  },
  'SA': {
    quickSelect: [4000, 8000, 12000, 16000, 24000, 32000, 48000, 80000],
    min: 1600,
    max: 160000,
    step: 400,
    tiers: {
      budget: { min: 1600, max: 8000, label: 'Budget-friendly' },
      moderate: { min: 8000, max: 24000, label: 'Moderate' },
      comfortable: { min: 24000, max: 48000, label: 'Comfortable' },
      premium: { min: 48000, max: 80000, label: 'Premium' },
      luxury: { min: 80000, max: 160000, label: 'Luxury' }
    }
  },
  'IL': {
    quickSelect: [400, 800, 1200, 1600, 2400, 3200, 4800, 8000],
    min: 160,
    max: 16000,
    step: 40,
    tiers: {
      budget: { min: 160, max: 800, label: 'Budget-friendly' },
      moderate: { min: 800, max: 2400, label: 'Moderate' },
      comfortable: { min: 2400, max: 4800, label: 'Comfortable' },
      premium: { min: 4800, max: 8000, label: 'Premium' },
      luxury: { min: 8000, max: 16000, label: 'Luxury' }
    }
  },
  
  // South America
  'BR': {
    quickSelect: [500, 1000, 1500, 2000, 3000, 4000, 6000, 10000],
    min: 200,
    max: 20000,
    step: 50,
    tiers: {
      budget: { min: 200, max: 1000, label: 'Budget-friendly' },
      moderate: { min: 1000, max: 3000, label: 'Moderate' },
      comfortable: { min: 3000, max: 6000, label: 'Comfortable' },
      premium: { min: 6000, max: 10000, label: 'Premium' },
      luxury: { min: 10000, max: 20000, label: 'Luxury' }
    }
  },
  'AR': {
    quickSelect: [50000, 100000, 150000, 200000, 300000, 400000, 600000, 1000000],
    min: 20000,
    max: 2000000,
    step: 5000,
    tiers: {
      budget: { min: 20000, max: 100000, label: 'Budget-friendly' },
      moderate: { min: 100000, max: 300000, label: 'Moderate' },
      comfortable: { min: 300000, max: 600000, label: 'Comfortable' },
      premium: { min: 600000, max: 1000000, label: 'Premium' },
      luxury: { min: 1000000, max: 2000000, label: 'Luxury' }
    }
  },
  'CL': {
    quickSelect: [100000, 200000, 300000, 400000, 600000, 800000, 1200000, 2000000],
    min: 40000,
    max: 4000000,
    step: 10000,
    tiers: {
      budget: { min: 40000, max: 200000, label: 'Budget-friendly' },
      moderate: { min: 200000, max: 600000, label: 'Moderate' },
      comfortable: { min: 600000, max: 1200000, label: 'Comfortable' },
      premium: { min: 1200000, max: 2000000, label: 'Premium' },
      luxury: { min: 2000000, max: 4000000, label: 'Luxury' }
    }
  },
  'CO': {
    quickSelect: [500000, 1000000, 1500000, 2000000, 3000000, 4000000, 6000000, 10000000],
    min: 200000,
    max: 20000000,
    step: 50000,
    tiers: {
      budget: { min: 200000, max: 1000000, label: 'Budget-friendly' },
      moderate: { min: 1000000, max: 3000000, label: 'Moderate' },
      comfortable: { min: 3000000, max: 6000000, label: 'Comfortable' },
      premium: { min: 6000000, max: 10000000, label: 'Premium' },
      luxury: { min: 10000000, max: 20000000, label: 'Luxury' }
    }
  },
  'PE': {
    quickSelect: [400, 800, 1200, 1600, 2400, 3200, 4800, 8000],
    min: 160,
    max: 16000,
    step: 40,
    tiers: {
      budget: { min: 160, max: 800, label: 'Budget-friendly' },
      moderate: { min: 800, max: 2400, label: 'Moderate' },
      comfortable: { min: 2400, max: 4800, label: 'Comfortable' },
      premium: { min: 4800, max: 8000, label: 'Premium' },
      luxury: { min: 8000, max: 16000, label: 'Luxury' }
    }
  },
  
  // Africa
  'ZA': {
    quickSelect: [2000, 4000, 6000, 8000, 12000, 16000, 24000, 40000],
    min: 800,
    max: 80000,
    step: 200,
    tiers: {
      budget: { min: 800, max: 4000, label: 'Budget-friendly' },
      moderate: { min: 4000, max: 12000, label: 'Moderate' },
      comfortable: { min: 12000, max: 24000, label: 'Comfortable' },
      premium: { min: 24000, max: 40000, label: 'Premium' },
      luxury: { min: 40000, max: 80000, label: 'Luxury' }
    }
  },
  
  // Other
  'PK': {
    quickSelect: [15000, 30000, 45000, 60000, 90000, 120000, 180000, 300000],
    min: 6000,
    max: 600000,
    step: 1500,
    tiers: {
      budget: { min: 6000, max: 30000, label: 'Budget-friendly' },
      moderate: { min: 30000, max: 90000, label: 'Moderate' },
      comfortable: { min: 90000, max: 180000, label: 'Comfortable' },
      premium: { min: 180000, max: 300000, label: 'Premium' },
      luxury: { min: 300000, max: 600000, label: 'Luxury' }
    }
  },
  'BD': {
    quickSelect: [10000, 20000, 30000, 40000, 60000, 80000, 120000, 200000],
    min: 4000,
    max: 400000,
    step: 1000,
    tiers: {
      budget: { min: 4000, max: 20000, label: 'Budget-friendly' },
      moderate: { min: 20000, max: 60000, label: 'Moderate' },
      comfortable: { min: 60000, max: 120000, label: 'Comfortable' },
      premium: { min: 120000, max: 200000, label: 'Premium' },
      luxury: { min: 200000, max: 400000, label: 'Luxury' }
    }
  },
  'RU': {
    quickSelect: [8000, 16000, 24000, 32000, 48000, 64000, 96000, 160000],
    min: 3200,
    max: 320000,
    step: 800,
    tiers: {
      budget: { min: 3200, max: 16000, label: 'Budget-friendly' },
      moderate: { min: 16000, max: 48000, label: 'Moderate' },
      comfortable: { min: 48000, max: 96000, label: 'Comfortable' },
      premium: { min: 96000, max: 160000, label: 'Premium' },
      luxury: { min: 160000, max: 320000, label: 'Luxury' }
    }
  },
  'TR': {
    quickSelect: [3000, 6000, 9000, 12000, 18000, 24000, 36000, 60000],
    min: 1200,
    max: 120000,
    step: 300,
    tiers: {
      budget: { min: 1200, max: 6000, label: 'Budget-friendly' },
      moderate: { min: 6000, max: 18000, label: 'Moderate' },
      comfortable: { min: 18000, max: 36000, label: 'Comfortable' },
      premium: { min: 36000, max: 60000, label: 'Premium' },
      luxury: { min: 60000, max: 120000, label: 'Luxury' }
    }
  },
};

/**
 * Get budget ranges for a country
 * @param {string} countryCode - ISO country code
 * @returns {Object} Budget range configuration
 */
export const getBudgetRanges = (countryCode) => {
  if (!countryCode) {
    return BUDGET_RANGES['US']; // Default to US
  }
  
  const ranges = BUDGET_RANGES[countryCode.toUpperCase()];
  if (ranges) {
    return ranges;
  }
  
  // For Eurozone countries not explicitly listed, use EU defaults
  const euroCountries = ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'FI', 'IE', 'PT', 'GR'];
  if (euroCountries.includes(countryCode.toUpperCase())) {
    return BUDGET_RANGES['EU'];
  }
  
  // Fallback to US ranges
  return BUDGET_RANGES['US'];
};

/**
 * Get quick-select budget values for a country
 * @param {string} countryCode - ISO country code
 * @returns {Array<number>} Array of budget values
 */
export const getQuickSelectBudgets = (countryCode) => {
  return getBudgetRanges(countryCode).quickSelect;
};

/**
 * Get minimum budget for a country
 * @param {string} countryCode - ISO country code
 * @returns {number} Minimum budget value
 */
export const getMinBudget = (countryCode) => {
  return getBudgetRanges(countryCode).min;
};

/**
 * Get maximum budget for a country
 * @param {string} countryCode - ISO country code
 * @returns {number} Maximum budget value
 */
export const getMaxBudget = (countryCode) => {
  return getBudgetRanges(countryCode).max;
};

/**
 * Get budget slider step size for a country
 * @param {string} countryCode - ISO country code
 * @returns {number} Step size
 */
export const getBudgetStep = (countryCode) => {
  return getBudgetRanges(countryCode).step;
};




